import { Buffer } from 'buffer'

export async function encipher(data: string, password: string) {
    const salt = window.crypto.getRandomValues(new Uint8Array(16));
    const iv = window.crypto.getRandomValues(new Uint8Array(12));
    const passwordKey = await getPasswordKey(password)
    const key = await deriveKey(passwordKey, salt, ["encrypt"])
    const encryptedContent = await window.crypto.subtle.encrypt(
        {
            name: "AES-GCM",
            iv: iv,
        },
        key,
        new TextEncoder().encode(data)
    );
    const encryptedContentArray = new Uint8Array(encryptedContent);
    const buffer = new Uint8Array(
        salt.byteLength + iv.byteLength + encryptedContentArray.byteLength
    );
    buffer.set(salt, 0);
    buffer.set(iv, salt.byteLength);
    buffer.set(encryptedContentArray, salt.byteLength + iv.byteLength);
    return toBase64Url(buffer);
}

export async function decipher(data: string, password: string) {
    const encryptedDataBuffer = fromBase64Url(data);
    const salt = encryptedDataBuffer.slice(0, 16);
    const iv = encryptedDataBuffer.slice(16, 16 + 12);
    const dataToDecrypt = encryptedDataBuffer.slice(16 + 12);
    const passwordKey = await getPasswordKey(password);
    const aesKey = await deriveKey(passwordKey, salt, ["decrypt"]);
    const decryptedContent = await window.crypto.subtle.decrypt(
        {
            name: "AES-GCM",
            iv: iv,
        },
        aesKey,
        dataToDecrypt
    );
    return new TextDecoder().decode(decryptedContent);
}

async function getPasswordKey(password: string) {
    return window.crypto.subtle.importKey(
        "raw",
        new TextEncoder().encode(password),
        "PBKDF2",
        false,
        ["deriveKey"]
    );
}

async function deriveKey(passwordKey: CryptoKey, salt: BufferSource, keyUsage: KeyUsage[]) {
    return window.crypto.subtle.deriveKey(
        {
            name: "PBKDF2",
            salt: salt,
            iterations: 250000,
            hash: "SHA-256",
        },
        passwordKey,
        { name: "AES-GCM", length: 256 },
        false,
        keyUsage
    );
}

function toBase64Url(byteArray: Uint8Array) {
    const buffer = Buffer.from(byteArray)
    return encode(buffer)
}

function fromBase64Url(base64urlString: string) {
    return toBuffer(base64urlString)
}

function encode(input: string | Buffer, encoding: BufferEncoding = "utf8"): string {
    if (Buffer.isBuffer(input)) {
        return fromBase64(input.toString("base64"));
    }
    return fromBase64(Buffer.from(input as string, encoding).toString("base64"));
};

function toBase64(base64url: string | Buffer): string {
    base64url = base64url.toString();
    return padString(base64url)
        .replace(/-/g, "+")
        .replace(/_/g, "/");
}

function fromBase64(base64: string): string {
    return base64
        .replace(/=/g, "")
        .replace(/\+/g, "-")
        .replace(/\//g, "_");
}

function toBuffer(base64url: string): Buffer {
    return Buffer.from(toBase64(base64url), "base64");
}

function padString(input: string): string {
    let segmentLength = 4;
    let stringLength = input.length;
    let diff = stringLength % segmentLength;

    if (!diff) {
        return input;
    }

    let position = stringLength;
    let padLength = segmentLength - diff;
    let paddedStringLength = stringLength + padLength;
    let buffer = Buffer.alloc(paddedStringLength);

    buffer.write(input);

    while (padLength--) {
        buffer.write("=", position++);
    }

    return buffer.toString();
}